import React from "react";
import { cn } from "../utils/cn";

type ToggleGroupContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
};

const ToggleGroupContext = React.createContext<ToggleGroupContextValue>({});

type BaseProps = {
  children: React.ReactNode;
  className?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

const Base = ({
  children,
  className,
  value,
  defaultValue,
  onValueChange,
}: BaseProps) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const currentValue = value ?? internalValue;

  return (
    <ToggleGroupContext.Provider
      value={{
        value: currentValue,
        onValueChange: (next) => {
          if (value === undefined) setInternalValue(next);
          onValueChange?.(next);
        },
      }}
    >
      <div
        className={cn(
          [
            "rounded-[8px]",
            "inline-flex",
            "justify-evenly",
            "bg-slate-100",
            "p-1",
            "min-h-[56px]",
          ],
          className,
        )}
        role="group"
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
};

type ItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value: string;
};

const Item = ({ children, className, value, onClick, ...props }: ItemProps) => {
  const ctx = React.useContext(ToggleGroupContext);
  const isActive = ctx.value === value;

  return (
    <button
      type="button"
      aria-pressed={isActive}
      data-state={isActive ? "on" : "off"}
      className={cn(
        [
          "bg-slate-100 text-slate-600",
          "max-w-full flex text-[14px] leading-none items-center justify-center",
          "ml-[1px] flex-1 min-h-[40px] px-1 whitespace-nowrap",
          "first:ml-0 first:rounded-tl-[4px] first:rounded-bl-[4px]",
          "last:rounded-tr-[4px] last:rounded-br-[4px]",
          "focus:z-[1]",
          "hover:bg-seaBlue85",
          "data-[state=on]:bg-slate-600 data-[state=on]:text-white data-[state=on]:rounded-[4px]",
        ],
        className,
      )}
      onClick={(e) => {
        onClick?.(e);
        ctx.onValueChange?.(value);
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export const ToggleGroup = {
  Base,
  Item,
};
